import { fg } from '@atlaskit/platform-feature-flags';
import { AtlassianIcon } from '@atlaskit/logo';
import {
	type Product,
	type ProductSelectOption,
	type InviteProduct,
	type Collections,
	CollectionType,
} from '../../types';
import {
	getProductById,
	JiraWorkManagementProduct,
	JiraSoftwareProduct,
	JiraServiceManagementProduct,
	getProductTitle,
} from '../../utils';
import { getTwcProducts } from '../../utils/twcForCoreInvites/getTwcProducts';

const defaultProduct = (id: string, label: string) => ({
	id,
	label,
	landingPath: '',
	directAccessLocation: 'others.core-invites',
	icon: AtlassianIcon,
});

export const mapProductSelectOptionsToProducts = (options: ProductSelectOption[]): Product[] => {
	const hasBothJSWAndJWM =
		options.find((option) => option.value === JiraWorkManagementProduct.id) &&
		options.find((option) => option.value === JiraSoftwareProduct.id);

	return options.map((option) => {
		const product = getProductById(option.value);
		// If has both JSW and JWM in selected products
		// then return the original JiraWorkManagementProduct that has Jira Work Management label and JWM Icon
		if (hasBothJSWAndJWM && option.value === JiraWorkManagementProduct.id) {
			return JiraWorkManagementProduct;
		}
		return product || defaultProduct(option.value, option.label);
	});
};

export const mapInviteProductsToProducts = (inviteProducts: InviteProduct[]): Product[] => {
	const hasBothJSWAndJWM =
		inviteProducts.find((product) => product.id === JiraWorkManagementProduct.id) &&
		inviteProducts.find((product) => product.id === JiraSoftwareProduct.id);

	return inviteProducts.map((option) => {
		const product = getProductById(option.id);
		// If has both JSW and JWM in available products
		// then return the original JiraWorkManagementProduct that has Jira Work Management label and JWM Icon
		if (hasBothJSWAndJWM && option.id === JiraWorkManagementProduct.id) {
			return JiraWorkManagementProduct;
		}
		return product || defaultProduct(option.id, option.name);
	});
};

export const mapProductsToProductSelectOptions = (
	availableProducts: Product[],
	productId?: string,
	jiraSubProduct?: string,
	collections: Collections = {},
): ProductSelectOption[] => {
	/*
	 Puma experiment: Puma-54 TWC for Core Invites
	 go/puma-experiments
	*/
	const { isTwcPreselected, twcProducts } = getTwcProducts(
		availableProducts,
		productId,
		jiraSubProduct,
		collections,
	);

	if (isTwcPreselected) {
		return availableProducts.map((product) => {
			const isTeamworkProduct = twcProducts.includes(product.id);
			return {
				value: product.id,
				label: product.label,
				group: isTeamworkProduct ? CollectionType.TEAMWORK_COLLECTION : undefined,
				isReadOnly: isTeamworkProduct,
			};
		});
	}

	return availableProducts.map((product) => {
		return {
			value: product.id,
			label: product.label,
		};
	});
};

export const mapProductIdsToProductSelectOptions = (ids: string[]): ProductSelectOption[] => {
	return ids.map((id) => ({ value: id, label: getProductTitle(id) || id }));
};

/**
 * @private
 * @deprecated This function has been deprecated as part of PUMA-420 refactoring.
 * Its logic has been merged directly into `getInitialSelectedProducts` for better maintainability.
 * Use `getInitialSelectedProducts` instead for determining default product selections.
 *
 * @see {@link getInitialSelectedProducts} - Use this function instead
 */
export const getDefaultSelectedProductsDeprecated = (
	availableProductOptions: ProductSelectOption[],
	productId: string,
	jiraSubProduct?: string,
	collections: Collections = {},
) => {
	/*
	 Puma experiment: Puma-54 TWC for Core Invites
	 go/puma-experiments
	*/
	const { isTwcPreselected, twcProducts } = getTwcProducts(
		availableProductOptions,
		productId,
		jiraSubProduct,
		collections,
	);

	if (isTwcPreselected) {
		return twcProducts.map((value) => ({ label: value, value }));
	}

	if (jiraSubProduct) {
		const jiraSubProducts = availableProductOptions.filter(
			(product) =>
				product.value === jiraSubProduct || product.value === JiraWorkManagementProduct.id,
		);
		// handle case when the current product is JWM but JWM is not in the invite-capabilites response
		if (jiraSubProducts.length === 0) {
			const jiraSoftware = availableProductOptions.find(
				(item) => item.value === JiraSoftwareProduct.id,
			);
			if (jiraSoftware) {
				return [jiraSoftware];
			}
			const jiraServiceManagement = availableProductOptions.find(
				(item) => item.value === JiraServiceManagementProduct.id,
			);
			if (jiraServiceManagement) {
				return [jiraServiceManagement];
			}
			// fallthrough when there are no jira product capabilities
		} else {
			return jiraSubProducts;
		}
	}
	if (availableProductOptions.length === 1) {
		// If there's exactly one product subscription, we should use it when sending
		// the invite
		return availableProductOptions;
	}
	const currentProducts = availableProductOptions.filter((product) =>
		product.value.includes(productId),
	);
	const fallbackProductList =
		productId !== 'platform' ? [{ value: productId, label: getProductTitle(productId) }] : [];
	return currentProducts.length > 0 ? currentProducts : fallbackProductList;
};

const isNotJiraCore = (product: string) => product !== JiraWorkManagementProduct.id;

const isAnyJira = (product: string) => product.startsWith('jira');

const isNonCoreJira = (product: string) => isAnyJira(product) && isNotJiraCore(product);

export const hasNonCoreJira = (products: string[]) => products?.some(isNonCoreJira) || false;

/**
 * Removes jira-core from the selected products list if other Jira products are present,
 * since jira-core is implied when other Jira products are selected.
 *
 * @param selectedProducts - Array of product IDs
 * @returns Array of product IDs with jira-core removed only if other Jira products are present
 *
 * @example
 * // jira-core is removed because jira-software is present
 * removeJiraCoreIfImplied(['jira-software', 'jira-core', 'confluence'])
 * // Returns: ['jira-software', 'confluence']
 *
 * @example
 * // jira-core is NOT removed because no other Jira products are present
 * removeJiraCoreIfImplied(['jira-core', 'confluence'])
 * // Returns: ['jira-core', 'confluence']
 *
 * @example
 * // No change when jira-core is not in the list
 * removeJiraCoreIfImplied(['jira-software', 'confluence'])
 * // Returns: ['jira-software', 'confluence']
 */
export const removeJiraCoreIfImplied = (selectedProducts: string[]): string[] =>
	hasNonCoreJira(selectedProducts) ? selectedProducts.filter(isNotJiraCore) : selectedProducts;

export const getInitialSelectedProducts = ({
	productOptions,
	productId,
	jiraSubProduct,
	collections,
}: {
	productOptions: Product[];
	productId: string;
	jiraSubProduct?: string;
	collections: Collections;
}): string[] => {
	if (fg('core_invites_refactoring_initial_selected_products')) {
		/*
			Puma experiment: Puma - 54 TWC for Core Invites
			go / puma - experiments
		*/
		const { isTwcPreselected, twcProducts } = getTwcProducts(
			productOptions,
			productId,
			jiraSubProduct,
			collections,
		);

		if (isTwcPreselected) {
			return twcProducts;
		}

		const productOptionIds = productOptions.map(({ id }) => id);

		if (jiraSubProduct) {
			const jiraSubProducts = productOptionIds.filter(
				(id) => id === jiraSubProduct || id === JiraWorkManagementProduct.id,
			);
			// handle case when the current product is JWM but JWM is not in the invite-capabilites response
			if (jiraSubProducts.length === 0) {
				const jiraSoftware = productOptionIds.find((id) => id === JiraSoftwareProduct.id);
				if (jiraSoftware) {
					return [jiraSoftware];
				}
				const jiraServiceManagement = productOptionIds.find(
					(id) => id === JiraServiceManagementProduct.id,
				);
				if (jiraServiceManagement) {
					return [jiraServiceManagement];
				}
				// fallthrough when there are no jira product capabilities
			} else {
				return removeJiraCoreIfImplied(jiraSubProducts);
			}
		}
		if (productOptionIds.length === 1) {
			// If there's exactly one product subscription, we should use it when sending
			// the invite
			return removeJiraCoreIfImplied(productOptionIds);
		}
		const currentProducts = productOptionIds.filter((id) => id.includes(productId));
		const fallbackProductList = productId !== 'platform' ? [productId] : [];
		return removeJiraCoreIfImplied(
			currentProducts.length > 0 ? currentProducts : fallbackProductList,
		);
	}

	return removeJiraCoreIfImplied(
		getDefaultSelectedProductsDeprecated(
			mapProductsToProductSelectOptions(productOptions, productId, jiraSubProduct, collections),
			productId,
			jiraSubProduct,
			collections,
		).map((product) => product.value),
	);
};
